import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { ActionsCreateButton } from './Actions.createButton';
import { QueryKey } from '@/Config/api/queryKey';
import { ActionDto, AIActionUsageStatsDto } from '@/Domain/api/Action/dto/Action.dto';
import { CampaignDto } from '@/Domain/api/Action/dto/Campaign.dto';
import { ShopifyMcpDto } from '@/Domain/api/Action/dto/ShopifyMcp.dto';
import { AiActionList } from '@/Domain/components/Action/AiActionList/AiActionList';
import { AppActionsList } from '@/Domain/components/Action/AppActionList/AppActionList';
import { DiscordTemplateGalleryDialog } from '@/Domain/components/Action/DiscordTemplates/DiscordTemplateGallery.dialog';
import { GoogleSheetsConnectDialog } from '@/Domain/components/Action/GoogleSheets/GoogleSheetsConnect.dialog';
import { CreateHttpRequestActionDialog } from '@/Domain/components/Action/HttpRequestActionFromText/CreateHttpRequestAction.modal';
import { HttpRequestActionSheet } from '@/Domain/components/Action/HttpRequestActionSheet/HttpRequestAction.sheet';
import { HubSpotTemplateGalleryDialog } from '@/Domain/components/Action/HubSpotTemplates/HubSpotTemplateGallery.dialog';
import { KnowledgeBaseActionSheet } from '@/Domain/components/Action/KnowledgeBaseAction/KnowledgeBaseAction.sheet';
import { RemoteMcpActionSheet } from '@/Domain/components/Action/RemoteMCPAction/RemoteMcpAction.sheet';
import { ShopifyMcpActionDialog } from '@/Domain/components/Action/ShopifyMcpAction/ShopifyMcpAction.dialog';
import { ReadDocumentationButton } from '@/Domain/components/Integration/components/ReadDocumentationButton';
import { useTrackPageVisit } from '@/Domain/components/OnboardingProgress';
import { Field, FieldDescription, FieldLabel } from '@/Domain/components/Shared/Field/Field';
import { Separator } from '@/Domain/components/Shared/Separator/Separator';
import { ActionTypeEnum } from '@/Domain/enum/ActionType.enum';
import { MODAL_NAMES, useModalStore } from '@/Domain/store/modal.store';
import {
  APP_GOOGLE_SHEETS,
  APP_HUBSPOT,
  APP_OTHER_API_ID,
  APP_OTHER_MCP,
  APP_SHOPIFY,
  CHANNEL_API,
  CHANNEL_DISCORD,
  ONBOARDING_APP_TO_MCP_CATALOG_NAME,
  OPEN_MODAL_QUERY_PARAM,
} from '@/UI/GettingStarted/components/onboardingOptions';

type Props = {
  actions: ActionDto[];
  stats?: Record<string, AIActionUsageStatsDto>;
};

export const ActionsPage = ({ actions, stats }: Props) => {
  const queryClient = useQueryClient();
  useTrackPageVisit('actions');

  const { t } = useTranslation('actions_page');
  const campaigns = queryClient.getQueryData<CampaignDto[]>([QueryKey.campaign.list]);

  const { data: knowledgeBaseTags, isLoading: isKnowledgeBaseTagsLoading } = useQuery<string[]>({
    queryKey: [QueryKey.knowledgeBase.tags],
  });

  const { toggleModal } = useModalStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const openParam = searchParams.get(OPEN_MODAL_QUERY_PARAM);
  const handledOpenRef = useRef<string | null>(null);

  const createShopifyMcpForDeepLinkMutation = useMutation<ShopifyMcpDto, unknown, void>({
    mutationKey: [QueryKey.actions.shopifyMcp.create],
  });

  useEffect(() => {
    if (!openParam || handledOpenRef.current === openParam) return;
    handledOpenRef.current = openParam;

    const clearOpenParam = () =>
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete(OPEN_MODAL_QUERY_PARAM);
          return next;
        },
        { replace: true },
      );

    const catalogEntryName = ONBOARDING_APP_TO_MCP_CATALOG_NAME.get(openParam);
    if (catalogEntryName) {
      toggleModal(MODAL_NAMES.REMOTE_MCP_DIALOG, { catalogEntryName });
      clearOpenParam();
      return;
    }

    switch (openParam) {
      case APP_HUBSPOT:
        toggleModal(MODAL_NAMES.HUBSPOT_TEMPLATE_GALLERY_DIALOG);
        clearOpenParam();
        break;
      case CHANNEL_DISCORD:
        toggleModal(MODAL_NAMES.DISCORD_TEMPLATE_GALLERY_DIALOG);
        clearOpenParam();
        break;
      case APP_GOOGLE_SHEETS:
        toggleModal(MODAL_NAMES.GOOGLE_SHEETS_DIALOG);
        clearOpenParam();
        break;
      case APP_SHOPIFY: {
        const existingShopifyAction = (actions || []).find(
          (action) => action.type === ActionTypeEnum.ShopifyRemoteMcp,
        );
        if (existingShopifyAction) {
          clearOpenParam();
          break;
        }
        createShopifyMcpForDeepLinkMutation
          .mutateAsync()
          .then((shopifyMcpAction) => {
            toggleModal(MODAL_NAMES.SHOPIFY_MCP_DIALOG, { shopifyMcpAction });
          })
          .finally(clearOpenParam);
        break;
      }
      case CHANNEL_API:
      case APP_OTHER_API_ID:
        toggleModal(MODAL_NAMES.CREATE_HTTP_REQUEST_ACTION_DIALOG);
        clearOpenParam();
        break;
      case 'remote_mcp':
      case APP_OTHER_MCP:
        toggleModal(MODAL_NAMES.REMOTE_MCP_DIALOG);
        clearOpenParam();
        break;
      default:
        clearOpenParam();
        break;
    }
  }, [openParam, actions, toggleModal, setSearchParams, createShopifyMcpForDeepLinkMutation]);

  return (
    <div className="w-full max-w-7xl mx-auto flex flex-col h-full">
      <div className="flex items-center justify-between gap-4">
        <h1 className="text-lg font-semibold text-foreground">{t('title')}</h1>
        <ReadDocumentationButton href="https://docs.quickchat.ai/ai-agent/actions" />
      </div>

      <div className="space-y-8 mt-8 flex flex-col flex-1">
        {campaigns?.length && (
          <section>
            <Field>
              <FieldLabel>{t('quickchat_actions_title')}</FieldLabel>
              <FieldDescription>{t('quickchat_actions_subtitle')}</FieldDescription>
              <AppActionsList campaigns={campaigns || []} />
            </Field>
          </section>
        )}

        <Separator className="mt-7" />
        <section className="flex flex-col flex-1 mb-6">
          <Field>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex flex-col gap-1.5">
                <FieldLabel>{t('api_actions_title')}</FieldLabel>
                <FieldDescription>{t('api_actions_description')}</FieldDescription>
              </div>
              <div className="flex items-center justify-between gap-3 sm:justify-end sm:gap-6">
                <ActionsCreateButton actions={actions || []} />
              </div>
            </div>
            <AiActionList actions={actions || []} stats={stats} />
          </Field>
        </section>
      </div>

      <HttpRequestActionSheet />
      {!isKnowledgeBaseTagsLoading ? (
        <KnowledgeBaseActionSheet knowledgeBaseTags={knowledgeBaseTags!} />
      ) : null}
      <ShopifyMcpActionDialog />
      <RemoteMcpActionSheet />
      <CreateHttpRequestActionDialog />
      <HubSpotTemplateGalleryDialog />
      <DiscordTemplateGalleryDialog />
      <GoogleSheetsConnectDialog />
    </div>
  );
};
